import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { ShopInformationApi } from 'api';
import type { ShopInformation } from 'api/methods/ShopInformationApi';

/**
 * [ShopInformation] Query key 생성
 * --
 */
export const shopInformationQueryKeys = createQueryKeys('shopInformation', {
  getShopInformation: (
    idx: number,
    params?: { [key: string]: string | number }
  ) => [idx, params],
  getShopInformations: (params?: { [key: string]: string | number }) => [
    params,
  ],
  getShops: (params?: { [key: string]: string | number }) => ['shops', params],
  getStores: (params?: { [key: string]: string | number }) => [
    'stores',
    params,
  ],
});

/**
 * [ShopInformation] 쇼/브/매/지 전체 조회
 * --
 */
export const useGetShopInformations = (
  params?: {
    [key: string]: string | number;
  },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: shopInformationQueryKeys.getShopInformations(params).queryKey,
    queryFn: () => ShopInformationApi.getShopInformations(params),
    ...options,
  });

  return data;
};

/**
 * [ShopInformation] 쇼핑몰/브랜드 전체 조회
 * --
 */
export const useGetShops = (params?: { [key: string]: string | number }) => {
  const data = useQuery({
    queryKey: shopInformationQueryKeys.getShops(params).queryKey,
    queryFn: () => ShopInformationApi.getShopInformations(params),
    select: (result) => {
      const { status, message, data } = result;

      const shops = data?.filter(
        (item: ShopInformation) => item.type === 'SHOP'
      );

      return {
        status,
        message,
        data: shops,
      };
    },
  });

  return data;
};

/**
 * [ShopInformation] 매장 전체 조회
 * --
 */
export const useGetStores = (
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: shopInformationQueryKeys.getStores(params).queryKey,
    queryFn: () => ShopInformationApi.getShopInformations(params),
    select: (result) => {
      const { status, message, data } = result;

      const stores = data?.filter(
        (item: ShopInformation) => item.type === 'STORE'
      );

      return {
        status,
        message,
        data: stores,
      };
    },
    ...options,
  });

  return data;
};

/**
 * [ShopInformation] 쇼/브/매/지 상세 조회
 * --
 */
export const useGetShopInformation = (
  idx: number,
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: shopInformationQueryKeys.getShopInformation(idx, params).queryKey,
    queryFn: () => ShopInformationApi.getShopInformation(idx, params),
    enabled: !!idx,
    ...options,
  });

  return data;
};

/**
 * [ShopInformation] 쇼/브/매/지 생성
 * --
 */
export const useCreateShopInformation = (body: ShopInformation) => {
  const { mutateAsync: createShopInformation, ...result } = useMutation({
    mutationFn: (variables: ShopInformation) =>
      ShopInformationApi.createShopInformation(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { createShopInformation, ...result };
};

/**
 * [ShopInformation] 쇼/브/매/지 수정
 * --
 */
export const useUpdateShopInformation = (body: ShopInformation) => {
  const { mutateAsync: updateShopInformation, ...result } = useMutation({
    mutationFn: (variables: ShopInformation) =>
      ShopInformationApi.updateShopInformation(variables),
  });

  return { updateShopInformation, ...result };
};

/**
 * [ShopInformation] 쇼/브/매/지 삭제
 * --
 */
export const useDeleteShopInformation = (idx: number) => {
  const { mutateAsync: deleteShopInformation, ...result } = useMutation({
    mutationFn: (variables: number) =>
      ShopInformationApi.deleteShopInformation(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { deleteShopInformation, ...result };
};
